import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchProductById,
  updateProduct,
  clearCurrentProduct,
} from '../../store/productSlice';
import {
  fetchApplicationsByProduct,
  createApplication,
  deleteApplication,
} from '../../store/applicationSlice';
import { departmentAPI } from '../../api/services';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentProduct, loading: productLoading } = useAppSelector((state) => state.products);
  const { applications, loading: applicationsLoading } = useAppSelector((state) => state.applications);

  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [departments, setDepartments] = useState<Array<{ _id: string; name: string }>>([]);

  // Form state for creating application
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    applicationType: 'backend' as 'backend' | 'frontend_web' | 'mobile_ios' | 'mobile_android' | 'desktop' | 'service' | 'library' | 'other',
    visibility: 'public' as 'public' | 'private',
    status: 'planning',
    departmentId: '',
    tags: [] as string[],
    techStack: [] as string[],
    version: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [techStackInput, setTechStackInput] = useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById({ id, includeApplications: false }));
      dispatch(fetchApplicationsByProduct(id));
    }

    // Load departments
    loadDepartments();

    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [dispatch, id]);

  const loadDepartments = async () => {
    try {
      const response = await departmentAPI.getAllDepartments();
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      applicationType: 'backend',
      visibility: 'public',
      status: 'planning',
      departmentId: '',
      tags: [],
      techStack: [],
      version: '',
    });
    setTagInput('');
    setTechStackInput('');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleAddTechStack = () => {
    if (techStackInput.trim() && !formData.techStack.includes(techStackInput.trim())) {
      setFormData({ ...formData, techStack: [...formData.techStack, techStackInput.trim()] });
      setTechStackInput('');
    }
  };

  const handleRemoveTechStack = (tech: string) => {
    setFormData({ ...formData, techStack: formData.techStack.filter(t => t !== tech) });
  };

  const handleCreateApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setCreateLoading(true);

    try {
      // Find selected department to get its name
      const selectedDepartment = departments.find(d => d._id === formData.departmentId);
      if (!selectedDepartment) {
        alert('Please select a department');
        setCreateLoading(false);
        return;
      }

      await dispatch(createApplication({
        ...formData,
        productIds: [id],
        departmentName: selectedDepartment.name,
      })).unwrap();

      setShowCreateAppModal(false);
      resetForm();
      dispatch(fetchApplicationsByProduct(id));
    } catch (err: any) {
      console.error('Error creating application:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteApplication = async (appId: string) => {
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      try {
        await dispatch(deleteApplication(appId)).unwrap();
        if (id) {
          dispatch(fetchApplicationsByProduct(id));
        }
      } catch (err) {
        console.error('Error deleting application:', err);
      }
    }
  };

  const getApplicationTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      backend: 'Backend',
      frontend_web: 'Frontend Web',
      mobile_ios: 'Mobile iOS',
      mobile_android: 'Mobile Android',
      desktop: 'Desktop',
      service: 'Service',
      library: 'Library',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getApplicationTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      backend: 'bg-purple-100 text-purple-800',
      frontend_web: 'bg-blue-100 text-blue-800',
      mobile_ios: 'bg-pink-100 text-pink-800',
      mobile_android: 'bg-green-100 text-green-800',
      desktop: 'bg-indigo-100 text-indigo-800',
      service: 'bg-yellow-100 text-yellow-800',
      library: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (productLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!currentProduct) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Product not found
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Product Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/products')}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Products
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentProduct.name}</h1>
              <p className="text-gray-600">{currentProduct.description}</p>
            </div>
            <div className="flex gap-2">
              <span className={`px-3 py-1 text-sm rounded-full ${
                currentProduct.status === 'production' ? 'bg-green-100 text-green-800' :
                currentProduct.status === 'development' ? 'bg-blue-100 text-blue-800' :
                currentProduct.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {currentProduct.status}
              </span>
              <span className={`px-3 py-1 text-sm rounded-full ${
                currentProduct.visibility === 'public' ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {currentProduct.visibility}
              </span>
            </div>
          </div>

          {currentProduct.tags && currentProduct.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {currentProduct.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Priority</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">{currentProduct.priority}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Category</p>
              <p className="text-lg font-semibold text-gray-900">{currentProduct.category || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Owner</p>
              <p className="text-lg font-semibold text-gray-900">{currentProduct.owner.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(currentProduct.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Applications Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Applications</h2>
          <button
            onClick={() => setShowCreateAppModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Create Application
          </button>
        </div>

        {applicationsLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading applications...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <p className="text-gray-500">No applications yet. Create one to get started.</p>
              </div>
            ) : (
              applications.map((application) => (
                <div
                  key={application._id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{application.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${getApplicationTypeColor(application.applicationType)}`}>
                      {getApplicationTypeLabel(application.applicationType)}
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{application.description}</p>

                  {application.version && (
                    <div className="text-sm text-gray-500 mb-2">
                      <span className="font-medium">Version:</span> {application.version}
                    </div>
                  )}

                  {application.techStack && application.techStack.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {application.techStack.slice(0, 3).map((tech, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded">
                          {tech}
                        </span>
                      ))}
                      {application.techStack.length > 3 && (
                        <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded">
                          +{application.techStack.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mb-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Projects:</span> {application.stats?.totalProjects || 0}
                      </div>
                      <div>
                        <span className="font-medium">Bugs:</span> {application.stats?.activeBugs || 0}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/admin/applications/${application._id}`)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      View Projects
                    </button>
                    <button
                      onClick={() => handleDeleteApplication(application._id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Application Modal */}
      {showCreateAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create New Application</h2>
                <button
                  onClick={() => {
                    setShowCreateAppModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateApplication} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter application name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter application description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Application Type *</label>
                    <select
                      required
                      value={formData.applicationType}
                      onChange={(e) => setFormData({ ...formData, applicationType: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="backend">Backend</option>
                      <option value="frontend_web">Frontend Web</option>
                      <option value="mobile_ios">Mobile iOS</option>
                      <option value="mobile_android">Mobile Android</option>
                      <option value="desktop">Desktop</option>
                      <option value="service">Service</option>
                      <option value="library">Library</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="planning">Planning</option>
                      <option value="development">Development</option>
                      <option value="testing">Testing</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                    <select
                      value={formData.visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as 'public' | 'private' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 1.0.0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
                  <select
                    required
                    value={formData.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tech Stack</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={techStackInput}
                      onChange={(e) => setTechStackInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTechStack())}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add technologies..."
                    />
                    <button
                      type="button"
                      onClick={handleAddTechStack}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {formData.techStack.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.techStack.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1">
                          {tech}
                          <button
                            type="button"
                            onClick={() => handleRemoveTechStack(tech)}
                            className="hover:text-purple-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add tags..."
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.tags.map((tag, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-blue-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateAppModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createLoading ? 'Creating...' : 'Create Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
